import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import Benefits from '@/components/Benefits'
import Icon from '@/components/Icon'
import Select from '@/components/Select'
import { getChannelDetail } from '@/network/api/channel'
import { useUserStore } from '@/store/user'

import { Container, Content, Header, LeftActions, RightActions, SkeletonWrapper } from './styles'
import { useVisualDetail } from './useVisualDetail'

const VisualDetail = () => {
  const { channelId } = useParams()
  const navigate = useNavigate()
  const { userInfo } = useUserStore()
  const [content, setContent] = useState('')
  const [loading, setLoading] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const { handleSelectChange } = useVisualDetail({ contentRef })
  const [title, setTitle] = useState('') // 添加标题 state

  useEffect(() => {
    const fetchDetail = async () => {
      if (!channelId) return
      if (loading) return

      try {
        setLoading(true)
        const data = await getChannelDetail(channelId)
        setContent(data.channel.ext.noJsHtml)
      } catch (error) {
        console.error('Failed to fetch detail:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchDetail()
  }, [channelId])

  useEffect(() => {
    if (contentRef.current && content) {
      // 创建 shadow root
      const shadow = contentRef.current.attachShadow({ mode: 'open' })

      // 创建样式元素
      const style = document.createElement('style')
      style.textContent = `
        :host {
          display: block;
          width: 100%;
        }
      `

      // 创建内容容器
      const container = document.createElement('div')
      container.innerHTML = content

      // 添加样式和内容到 shadow DOM
      shadow.appendChild(style)
      shadow.appendChild(container)
    }
  }, [content])

  useEffect(() => {
    if (content) {
      // 解析 HTML 字符串以获取标题
      const parser = new DOMParser()
      const doc = parser.parseFromString(content, 'text/html')
      const h1 = doc.querySelector('h1')
      setTitle(h1?.textContent || '')
    }
  }, [content])

  return (
    <Container>
      <Header>
        <LeftActions>
          <button
            className="back-btn"
            onClick={() => navigate(`/home/${userInfo?.user.email.replace('@gmail.com', '')}`)}
          >
            <Icon type="home" />
          </button>
          <div>
            <Icon type="rightArrow" />
          </div>
          <div>{title}</div>
        </LeftActions>
        <RightActions>
          <Benefits>
            <Icon type="lightning" />
            <span>{userInfo?.benefit?.credits || '--'}</span>
          </Benefits>
          <Select
            options={[
              { label: 'Copy link', value: 'copyLink' },
              { label: 'Copy to clipboard', value: 'copyImg' },
            ]}
            onChange={handleSelectChange}
          >
            <div className="action-btn">
              <Icon type="share" />
              Share
            </div>
          </Select>

          <Select
            options={[
              { label: 'PNG', value: 'png' },
              { label: 'JPG', value: 'jpg' },
              { label: 'SVG', value: 'svg' },
              { label: 'HTML', value: 'html' },
            ]}
            onChange={handleSelectChange}
          >
            <div className="action-btn">
              <Icon type="download" />
              Export
            </div>
          </Select>
        </RightActions>
      </Header>

      <Content>
        {loading ? (
          <SkeletonWrapper>
            <div className="skeleton-header" />
            <div className="skeleton-paragraph">
              {Array(6)
                .fill(null)
                .map((_, index) => (
                  <div key={index} />
                ))}
            </div>
          </SkeletonWrapper>
        ) : (
          <div ref={contentRef} />
        )}
      </Content>
    </Container>
  )
}

export default VisualDetail
